import type { ChannelNatsCredentials } from "@/types";
import type { ProvisioningBlock } from "@/components/ProvisioningInstructionsDialog";
import { MANAGEMENT_URL } from "@/config/env";

// #186: Baut die Provisioning-Anweisung (ENV-Bundle + docker-run + Hints) für
// einen frisch erstellten Channel. Pure Funktion → isoliert testbar.

export interface CreatedChannelInfo {
  channelId: string;
  channelName: string;
  channelType: string;
  channelToken: string;
  natsCredentials: ChannelNatsCredentials | null;
}

export interface ChannelProvisioning {
  blocks: ProvisioningBlock[];
  runCommand: string;
  hints: string[];
}

// Default-Images pro bekanntem Channel-Type. Customer-Plugins (freier Type)
// kriegen einen Platzhalter — der Operator setzt sein eigenes Image ein.
// Über VITE_CHANNEL_IMAGE_<TYPE> überschreibbar (Customer-Hosting).
function imageForType(type: string): string {
  const envKey = `VITE_CHANNEL_IMAGE_${type.toUpperCase().replace(/[^A-Z0-9]/g, "_")}`;
  const fromEnv = (import.meta.env as Record<string, string | undefined>)[envKey];
  if (fromEnv) return fromEnv;
  if (type === "whatsapp") return "agent-mesh/whatsapp-channel:latest";
  return `<dein-${type}-channel-image>`;
}

// Gibt null zurück wenn keine NATS-Credentials da sind (Dev ohne NATS) —
// dann zeigt die UI nur den Bootstrap-Token.
export function channelProvisioning(info: CreatedChannelInfo): ChannelProvisioning | null {
  const creds = info.natsCredentials;
  if (!creds) return null;

  const envBlock = [
    `NATS_URL=${creds.natsUrl}`,
    `NATS_JWT=${creds.natsJwt}`,
    `NATS_SEED=${creds.natsSeed}`,
    `CHANNEL_ID=${info.channelId}`,
    // #202: channel-token + Master-URL für Attachment-Upload/-Download (HTTP).
    `CHANNEL_TOKEN=${info.channelToken}`,
    `MANAGEMENT_URL=${MANAGEMENT_URL}`,
  ].join("\n");

  const image = imageForType(info.channelType);
  const containerName = `channel-${info.channelName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || info.channelId.slice(0, 8)}`;

  const runCommand = [
    `docker run -d \\`,
    `  --name ${containerName} \\`,
    `  --env-file ./channel.env \\`,
    `  --restart unless-stopped \\`,
    `  -v ${containerName}-data:/app/channel-data \\`,
    `  ${image}`,
  ].join("\n");

  const hints: string[] = [
    "Speichere den Block als channel.env neben dem docker run.",
    `MANAGEMENT_URL ist auf ${MANAGEMENT_URL} gesetzt — anpassen, falls der Plugin-Container den Master unter einer anderen Adresse erreicht (z.B. im Docker-Netz).`,
    "CHANNEL_TOKEN wird nur HIER im Klartext gezeigt — bei Verlust über 'Rotate Credentials' neu ausstellen.",
    info.channelType === "whatsapp"
      ? `WhatsApp-QR scannen: docker logs -f ${containerName} (beim ersten Start).`
      : "Pairing/Setup ist plugin-spezifisch — siehe dein Channel-Plugin.",
    "Sobald der Container läuft, wechselt der Channel-Status auf 'online'.",
  ];

  return {
    blocks: [{ label: "NATS-Verbindung (channel.env)", value: envBlock }],
    runCommand,
    hints,
  };
}
